/**
 * usePermissions Hook — RBAC Phase 4
 *
 * Sources effective permissions from AppState (populated by GET /lender/me).
 * Replaces TEMP_ADMIN_USER_IDS hardcoded checks with DB-backed permission checks.
 *
 * @example
 * const { hasPermission, canAccessTier, isAdmin, permissions } = usePermissions();
 * if (hasPermission('member:view_pii')) { ... }
 * if (canAccessTier(3)) { ... }
 * if (isAdmin) { ... }
 */

import { useMemo } from 'react';
import useAppState from '../common/AppState';

/**
 * RBAC role codes that qualify as "internal platform admin" — used for
 * internal admin-only UI features (Data Health, Privacy KPIs, etc.).
 *
 * IMPORTANT: FI roles (FI_ADMIN, LENDER_MANAGER, etc.) are EXTERNAL
 * and must NOT be included here. FI_ADMIN manages their own org but
 * should not access internal VeraScore admin pages.
 */
const ADMIN_ROLE_CODES = [
  'VS_SUPER_ADMIN',
  'VS_SUPPORT_ADMIN',
  'VS_COMPLIANCE',
] as const;

/**
 * Role codes that qualify for Privacy KPI Dashboard access.
 * Replaces TEMP_ADMIN_USER_IDS + ADMIN_ROLES check.
 */
const PRIVACY_KPI_ROLE_CODES = [
  'VS_SUPER_ADMIN',
  'VS_SUPPORT_ADMIN',
  'VS_COMPLIANCE',
  'COMPLIANCE_AUDITOR',
] as const;

export interface UsePermissionsResult {
  /** All effective permissions for the current user */
  permissions: string[];
  /** The user's role code (e.g. 'FI_ADMIN', 'LOAN_OFFICER') */
  roleCode: string | undefined;
  /** The user's org type ('FI' or 'INTERNAL') */
  orgType: string | undefined;
  /** Max data tier accessible (1-5) */
  maxDataTier: number;
  /** Check if user has a specific permission */
  hasPermission: (permission: string) => boolean;
  /** Check if user has ALL of the specified permissions */
  hasAllPermissions: (...permissions: string[]) => boolean;
  /** Check if user has ANY of the specified permissions */
  hasAnyPermission: (...permissions: string[]) => boolean;
  /** Check if user can access a specific data tier */
  canAccessTier: (tier: number) => boolean;
  /** Whether the user is an internal platform admin (VS_SUPER_ADMIN, VS_SUPPORT_ADMIN, VS_COMPLIANCE) */
  isAdmin: boolean;
  /** Whether the user is an FI-level admin (FI_ADMIN — manages their org, not platform) */
  isFiAdmin: boolean;
  /** Whether the user can access Privacy KPI Dashboard */
  canAccessPrivacyKpis: boolean;
  /** Whether the user is a VeraScore internal user */
  isInternal: boolean;
  /** Whether permissions have been loaded (i.e. /lender/me has been called) */
  isLoaded: boolean;
}

/**
 * Hook to access the current user's RBAC permissions.
 *
 * Permissions are loaded from the backend via GET /lender/me and stored
 * in AppState.orgUser.permissions during authentication.
 */
export function usePermissions(): UsePermissionsResult {
  const { orgUser } = useAppState();

  return useMemo(() => {
    const permissions = orgUser?.permissions ?? [];
    const roleCode = orgUser?.role_code;
    const orgType = orgUser?.org_type;
    const maxDataTier = orgUser?.max_data_tier ?? 0;
    const isLoaded = !!orgUser && Array.isArray(orgUser.permissions);

    const permissionSet = new Set(permissions);

    const hasPermission = (permission: string): boolean =>
      permissionSet.has(permission);

    const hasAllPermissions = (...perms: string[]): boolean =>
      perms.every(p => permissionSet.has(p));

    const hasAnyPermission = (...perms: string[]): boolean =>
      perms.some(p => permissionSet.has(p));

    const canAccessTier = (tier: number): boolean =>
      maxDataTier >= tier;

    const isAdmin = ADMIN_ROLE_CODES.some(rc =>
      roleCode?.toUpperCase() === rc
    );

    const isInternal = orgType === 'INTERNAL';
    const isFiAdmin = roleCode?.toUpperCase() === 'FI_ADMIN';

    const canAccessPrivacyKpis =
      PRIVACY_KPI_ROLE_CODES.some(rc => roleCode?.toUpperCase() === rc) ||
      hasPermission('admin:privacy_kpi');

    return {
      permissions,
      roleCode,
      orgType,
      maxDataTier,
      hasPermission,
      hasAllPermissions,
      hasAnyPermission,
      canAccessTier,
      isAdmin,
      isFiAdmin,
      canAccessPrivacyKpis,
      isInternal,
      isLoaded,
    };
  }, [orgUser]);
}

export default usePermissions;
